import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createVNode as _createVNode, createTextVNode as _createTextVNode, normalizeProps as _normalizeProps, guardReactiveProps as _guardReactiveProps, withModifiers as _withModifiers, withKeys as _withKeys, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "text-center full-width" }
const _hoisted_2 = { class: "absolute-right flex items-center q-pr-sm" }

import { onMounted, ref, watch, computed } from "vue";
  import { useRouter } from "vue-router";

  import { useVehicleStore } from "src/stores/vehicle-store";
  import { useProposalStore } from "src/stores/proposal-store";
  import { useAuthStore } from "src/stores/auth-store";
  import { useWebSocketStore } from "src/stores/websocket-store";

  import DealerInfo from "src/types/dealer/dealer-info";
  import { useHideLoading, useLoading } from "src/functions";
  import { DealerService } from "src/services";

  
export default /*@__PURE__*/_defineComponent({
  __name: 'DealerPicker',
  emits: ['show'],
  setup(__props, { emit: __emit }) {

  const router = useRouter();
  const proposalStore = useProposalStore();
  const authStore = useAuthStore();
  const vehicleStore = useVehicleStore();
  const wsStore = useWebSocketStore();

  const isDealer = computed(() => authStore.isDealer);

  onMounted(() => {
    selectedDealers.value = authStore.selectedDealers;
    if (!isDealer.value) {
      useLoading("Getting Dealers");
      authStore.getDealers(asmOverride.value ?? undefined).then(res => {
        useHideLoading();
        selectAllDealers.value = selectedDealers.value && selectedDealers.value.length === dealers.value.length ? true : false
      })
    }
  });

  const emit = __emit;

  // ASM
  const asmOverrides = ref([
    { label: "Cameron Baker", value: 14250 },
    { label: "Michael Brown", value: 13430 },
    { label: "Adele Daniels", value: 13890 },
    { label: "David Hobin", value: 14408 },
    { label: "Anne MacTaggart", value: 14445 }, 
    { label: "Stuart Rowley", value: 14405 },
    { label: "Samuel Sharpe", value: 14493 },
    { label: "Lara McCarthy", value: 14555 }
  ])

  const asmOverride = ref<number | null>(authStore.asmOverride);
  const clearedASM = ref(false);
  watch(() => asmOverride.value, () => {
    if (!asmOverride.value) {
      clearedASM.value = true;
    }
    clearDealers();
    selectedDealers.value = null;
    selectAllDealers.value = false;
    selectedGroup.value = null;
    authStore.updateAsmOverride(asmOverride.value);
    useLoading("Getting Dealers");
    authStore.getDealers(asmOverride.value ?? undefined).then(res => {
      useHideLoading();
    })
  })

  // Dealer groups
  const groups = computed(() => {
    if (!authStore.dealerGroups) return [];

    // show all groups
    return authStore.dealerGroups.map(g => { return { label: g.group.dealerGroupName, value: g.group.dealerGroupId } });
  })
  const filteredGroups = ref(groups.value);
  function filterFnGroups(val: string, update: Function) {
    if (val === '') {
      update(() => {
        filteredGroups.value = groups.value;
      });
      return;
    }

    update(() => {
      filteredGroups.value = groups.value.filter(v => v.label.toLowerCase().includes(val.toLowerCase()));
    });
  }
  const selectedGroup = ref<number | null>(authStore.selectedGroup);
  watch(() => selectedGroup.value, () => {
    selectAllDealers.value = false;
    selectedDealers.value = null;
    authStore.updateSelectedGroup(selectedGroup.value);
    clearDealers();
  })

  // Dealers
  const dealers = computed(() => {
    if (selectedGroup.value) {
      return authStore.dealerGroups?.find(g => g.group.dealerGroupId === selectedGroup.value)?.dealers ?? [];
    }
    return authStore.dealers ? authStore.dealers.sort((a, b) => a.name < b.name ? -1 : 1) : []
  });
  watch(dealers, (newVal: DealerInfo[], oldVal: DealerInfo[]) => {
    if (newVal.length > oldVal.length) {
      emit("show", true);
    }
  });
  const filteredDealers = ref<DealerInfo[] | null>(null);
  function filterFn(val: string, update: Function) {
    if (val === '') {
      update(() => {
        filteredDealers.value = dealers.value;
      });
      return;
    }

    update(() => {
      const needle = val.toLowerCase();
      filteredDealers.value = dealers.value.filter(v => v.name.toLowerCase().includes(needle));
    });
  }

  const selectedDealers = ref<DealerInfo[] | null>(null);
  watch(() => selectedDealers.value, () => {
    selectAllDealers.value = selectedDealers.value && selectedDealers.value.length === dealers.value.length ? true : false;
  })
  const selectAllDealers = ref(selectedDealers.value && selectedDealers.value.length === dealers.value.length ? true : false);

  function toggleSelectAll() {
    selectAllDealers.value = !selectAllDealers.value;
    if (selectAllDealers.value === false) {
      selectedDealers.value = [];
    }
    else {
      selectedDealers.value = dealers.value;
    }
  }
  
  function selectDealers(dealers: DealerInfo[] | null) {
    authStore.selectDealers(dealers);
    proposalStore.setApplications([]);
    proposalStore.getApplications(30);
    proposalStore.setQuickQuotes([]);
    proposalStore.setBespokeRates(null);
    proposalStore.getQuickQuotes(30);
    vehicleStore.resetStockUpdate();
    vehicleStore.setVehicles([]);
    vehicleStore.getStockVehicles();
    wsStore.disconnect();
    for (const dealer of dealers!) {
      if (dealer.dealerId) {
        wsStore.connect(dealer.affiliateUserId.toString()).catch((err) => {
          console.error('WebSocket connection error:', err);
        });
      }
    }
    router.push({ name: "Customers" });
    emit("show", false);
  }

  function clearDealers() {
    authStore.selectDealers(null);
    vehicleStore.resetStockUpdate();
    vehicleStore.setVehicles([]);
    proposalStore.setApplications([]);
    proposalStore.setQuickQuotes([]);
    proposalStore.clearSubscription();
    proposalStore.setBespokeRates(null);
    router.push({ name: "Customers" });
  }

  function deselectDealer(dealer: DealerInfo) {
    selectedDealers.value = selectedDealers.value!.filter(d => d.dealerId !== dealer.dealerId);
  }

  function remove(event: { index: number, value: DealerInfo }) {
    if (deletePressed.value) {
      deletePressed.value = false;
      setTimeout(() => {
        selectedDealers.value!.push(event.value)
      }, 1)
    }
  }

  const deletePressed = ref(false);
  function deleteKeyDown(evt: KeyboardEvent) {
    deletePressed.value = true;
  }

  const displayValue = computed(() => {
    if (selectedDealers.value !== null && selectedDealers.value.length > 0) {
      return `${selectedDealers.value.length} Selected`;
    } else {
      return null;
    }
  });

  const placeholderText = computed(() => {
    return selectedDealers.value && selectedDealers.value.length > 0
      ? null
      : isDealer.value
        ? 'Select a branch...'
        : 'Dealers';
  });

  const title = computed(() => authStore.dealerGroup ? authStore.dealerGroup.dealerGroupName : 'Dealer Select')

  const showClose = computed(() => authStore.selectedDealers ? true : false)

  const buttonLabel = computed(() => {
    return !selectedDealers.value || selectedDealers.value.length === 0 ? `Select a ${isDealer.value ? 'branch' : 'dealer'}` : `Select ${selectedDealers.value.length} ${isDealer.value ? 'Branch' : 'Dealer'}${selectedDealers.value.length > 1 ? isDealer.value ? 'es' : 's' : ''}`
  })

return (_ctx: any,_cache: any) => {
  const _component_q_btn = _resolveComponent("q-btn")!
  const _component_q_card_section = _resolveComponent("q-card-section")!
  const _component_q_separator = _resolveComponent("q-separator")!
  const _component_q_select = _resolveComponent("q-select")!
  const _component_q_item_label = _resolveComponent("q-item-label")!
  const _component_q_item_section = _resolveComponent("q-item-section")!
  const _component_q_toggle = _resolveComponent("q-toggle")!
  const _component_q_item = _resolveComponent("q-item")!
  const _component_q_icon = _resolveComponent("q-icon")!
  const _component_q_list = _resolveComponent("q-list")!
  const _component_q_card = _resolveComponent("q-card")!

  return (_openBlock(), _createBlock(_component_q_card, {
    bordered: "",
    style: {"width":"700px","max-width":"100%","height":"unset"}
  }, {
    default: _withCtx(() => [
      _createVNode(_component_q_card_section, { class: "text-center text-h4 flex items-center" }, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_1, _toDisplayString(title.value), 1),
          _createElementVNode("div", _hoisted_2, [
            (showClose.value)
              ? (_openBlock(), _createBlock(_component_q_btn, {
                  key: 0,
                  flat: "",
                  round: "",
                  icon: "mdi-close",
                  onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('show', false)))
                }))
              : _createCommentVNode("", true)
          ])
        ]),
        _: 1
      }),
      _createVNode(_component_q_separator),
      (!isDealer.value)
        ? (_openBlock(), _createBlock(_component_q_card_section, {
            key: 0,
            class: "q-pb-none"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_q_select, {
                modelValue: asmOverride.value,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((asmOverride).value = $event)),
                options: asmOverrides.value,
                "emit-value": "",
                "bg-color": "white",
                "popup-content-class": "text-body2",
                "map-options": "",
                label: "ASM (optional)",
                outlined: "",
                dense: "",
                clearable: "",
                onClear: _cache[2] || (_cache[2] = ($event: any) => (asmOverride.value = null))
              }, null, 8, ["modelValue", "options"])
            ]),
            _: 1
          }))
        : _createCommentVNode("", true),
      (!isDealer.value)
        ? (_openBlock(), _createBlock(_component_q_card_section, {
            key: 1,
            class: "q-pb-none"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_q_select, {
                modelValue: selectedGroup.value,
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((selectedGroup).value = $event)),
                options: filteredGroups.value,
                "emit-value": "",
                "bg-color": "white",
                "popup-content-class": "text-body2 limit-dd-height",
                "map-options": "",
                label: "Dealer Group (optional)",
                outlined: "",
                dense: "",
                clearable: "",
                onClear: _cache[4] || (_cache[4] = ($event: any) => (selectedGroup.value = null)),
                "use-input": "",
                "input-debounce": "300",
                filter: true,
                onFilter: filterFnGroups
              }, null, 8, ["modelValue", "options"])
            ]),
            _: 1
          }))
        : _createCommentVNode("", true),
      _createVNode(_component_q_card_section, { class: "" }, {
        default: _withCtx(() => [
          _createVNode(_component_q_select, {
            outlined: "",
            dense: "",
            modelValue: selectedDealers.value,
            "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((selectedDealers).value = $event)),
            clearable: "",
            multiple: "",
            "use-input": "",
            "input-debounce": "300",
            filter: true,
            "display-value": displayValue.value,
            placeholder: placeholderText.value,
            onClear: clearDealers,
            onFilter: filterFn,
            onRemove: remove,
            onKeydown: _withKeys(_withModifiers(deleteKeyDown, ["stop"]), ["delete"]),
            "option-label": "name",
            options: filteredDealers.value ?? undefined,
            "popup-content-class": "text-body2 limit-dd-height"
          }, {
            option: _withCtx(({ itemProps, opt, selected, toggleOption, index }) => [
              (index === 0 && filteredDealers.value.length === dealers.value.length)
                ? (_openBlock(), _createBlock(_component_q_item, {
                    key: 0,
                    onClick: toggleSelectAll,
                    clickable: "",
                    active: selectAllDealers.value
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_q_item_section, null, {
                        default: _withCtx(() => [
                          _createVNode(_component_q_item_label, null, {
                            default: _withCtx(() => _cache[7] || (_cache[7] = [
                              _createTextVNode("Select All")
                            ])),
                            _: 1,
                            __: [7]
                          })
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_q_item_section, { side: "" }, {
                        default: _withCtx(() => [
                          _createVNode(_component_q_toggle, {
                            "model-value": selectAllDealers.value,
                            onClick: toggleSelectAll
                          }, null, 8, ["model-value"])
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }, 8, ["active"]))
                : _createCommentVNode("", true),
              _createVNode(_component_q_item, _normalizeProps(_guardReactiveProps(itemProps)), {
                default: _withCtx(() => [
                  _createVNode(_component_q_item_section, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_q_item_label, {
                        innerHTML: opt.name
                      }, null, 8, ["innerHTML"])
                    ]),
                    _: 2
                  }, 1024),
                  _createVNode(_component_q_item_section, { side: "" }, {
                    default: _withCtx(() => [
                      _createVNode(_component_q_toggle, {
                        "model-value": selected,
                        "onUpdate:modelValue": ($event: any) => (toggleOption(opt))
                      }, null, 8, ["model-value", "onUpdate:modelValue"])
                    ]),
                    _: 2
                  }, 1024)
                ]),
                _: 2
              }, 1040)
            ]),
            _: 1
          }, 8, ["modelValue", "display-value", "placeholder", "onKeydown", "options"])
        ]),
        _: 1
      }),
      _createVNode(_component_q_card_section, { class: "q-pt-none" }, {
        default: _withCtx(() => [
          _createVNode(_component_q_list, {
            separator: "",
            bordered: "",
            style: {"height":"234px","min-height":"234px","overflow-y":"scroll"}
          }, {
            default: _withCtx(() => [
              (!selectedDealers.value || selectedDealers.value.length === 0)
                ? (_openBlock(), _createBlock(_component_q_item, {
                    key: 0,
                    class: "flex items-center q-pl- text-body3"
                  }, {
                    default: _withCtx(() => _cache[8] || (_cache[8] = [
                      _createTextVNode(" None Selected ")
                    ])),
                    _: 1,
                    __: [8]
                  }))
                : _createCommentVNode("", true),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(selectedDealers.value, (dealer) => {
                return (_openBlock(), _createBlock(_component_q_item, { class: "flex items-center q-pl-sm text-body3" }, {
                  default: _withCtx(() => [
                    _createVNode(_component_q_item_section, null, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(dealer.name), 1)
                      ]),
                      _: 2
                    }, 1024),
                    _createVNode(_component_q_item_section, { side: "" }, {
                      default: _withCtx(() => [
                        _createVNode(_component_q_btn, {
                          flat: "",
                          round: "",
                          dense: "",
                          onClick: ($event: any) => (deselectDealer(dealer))
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_q_icon, {
                              name: "mdi-close-circle",
                              style: {"opacity":"0.6"}
                            })
                          ]),
                          _: 2
                        }, 1032, ["onClick"])
                      ]),
                      _: 2
                    }, 1024)
                  ]),
                  _: 2
                }, 1024))
              }), 256)),
              _createVNode(_component_q_separator)
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createVNode(_component_q_card_section, { class: "q-pt-none" }, {
        default: _withCtx(() => [
          _createVNode(_component_q_btn, {
            class: "brand-button full-width",
            color: "primary",
            disable: !selectedDealers.value || selectedDealers.value.length === 0,
            label: buttonLabel.value,
            onClick: _cache[6] || (_cache[6] = ($event: any) => {selectDealers(selectedDealers.value);})
          }, null, 8, ["disable", "label"])
        ]),
        _: 1
      })
    ]),
    _: 1
  }))
}
}

})